import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowRight, Check, ShieldCheck, Sparkles, Users, Wallet,
  Link as LinkIcon, MapPin, BarChart3, Network, Zap, FileSpreadsheet,
  ChevronDown, Building2, ScrollText, QrCode, Trophy,
  ListChecks, Lock, MessageCircle, Tag, FileText, Play,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtBRL = (n) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n || 0);

const HEADER_HEIGHT = 96;

const Section = ({ id, children, className = '' }) => (
  <section id={id} className={`px-6 md:px-12 lg:px-20 ${className}`}>
    <div className="max-w-7xl mx-auto">{children}</div>
  </section>
);

const EyebrowLabel = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-emerald-700 font-manrope">
    <span className="h-px w-6 bg-emerald-300" />
    {children}
  </span>
);

// ---------------------------------------------------------------------------
// HEADER
// ---------------------------------------------------------------------------
const Header = () => (
  <header
    className="fixed top-0 left-0 right-0 z-50 bg-white/85 backdrop-blur-md border-b border-slate-200/60"
    style={{ height: HEADER_HEIGHT }}
  >
    <div className="max-w-7xl mx-auto h-full flex items-center justify-between px-6 md:px-12 lg:px-20">
      <Link to="/" className="flex items-center" data-testid="logo-link">
        <img src="/brand/meadd-logo.png" alt="MeAdd" className="h-[3.5rem] md:h-[4.25rem] object-contain" />
      </Link>
      <nav className="hidden md:flex items-center gap-8 font-manrope text-sm font-medium text-slate-700">
        <a href="#recursos" className="hover:text-emerald-600 transition-colors">Recursos</a>
        <a href="#fluxo" className="hover:text-emerald-600 transition-colors">Como funciona</a>
        <a href="#planos" className="hover:text-emerald-600 transition-colors">Planos</a>
        <a href="#faq" className="hover:text-emerald-600 transition-colors">FAQ</a>
      </nav>
      <div className="flex items-center gap-2">
        <Button variant="ghost" asChild size="sm" className="font-manrope">
          <Link to="/login" data-testid="header-login-btn">Entrar</Link>
        </Button>
        <Button asChild size="sm" className="bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-slate-900 font-manrope font-bold rounded-full px-5 shadow-md shadow-amber-500/30">
          <Link to="/signup" data-testid="header-signup-btn">
            Testar grátis <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Link>
        </Button>
      </div>
    </div>
  </header>
);

// ---------------------------------------------------------------------------
// HERO
// ---------------------------------------------------------------------------
const Hero = () => (
  <Section className="pt-36 md:pt-44 pb-16">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-7 space-y-7">
        <EyebrowLabel>Plataforma para campanhas políticas</EyebrowLabel>
        <h1 className="font-outfit font-bold text-5xl md:text-6xl lg:text-[4.5rem] leading-[1.05] tracking-tight text-slate-900">
          Do cadastro de liderança à mobilização de apoiadores,{' '}
          <span className="text-emerald-600">tudo conectado</span>.
        </h1>
        <p className="text-lg md:text-xl text-slate-600 font-manrope max-w-2xl leading-relaxed">
          Um fluxo simples, seguro e rastreável que transforma cada liderança em um ponto
          de mobilização da sua <strong className="text-slate-800 font-semibold">campanha política</strong>.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 pt-2 items-start">
          <div className="relative inline-block">
            <Button asChild size="lg" className="bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-slate-900 font-manrope font-bold rounded-full px-7 h-12 shadow-lg shadow-amber-500/40 hover:shadow-xl hover:shadow-amber-500/50 hover:-translate-y-0.5 transition-all duration-200">
              <Link to="/signup" data-testid="hero-signup-btn">
                Testar grátis agora <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] uppercase tracking-wider font-bold font-manrope bg-slate-900 text-amber-300 px-2.5 py-1 rounded-full shadow-md border border-amber-500/40">
              ✨ 7 dias grátis · Sem cartão
            </span>
          </div>
          <Button asChild variant="ghost" size="lg" className="font-manrope text-slate-700 hover:text-emerald-700 hover:bg-emerald-50 rounded-full px-5 h-12">
            <a href="#fluxo" data-testid="hero-watch-btn">
              Ver na prática
              <span className="ml-2 h-7 w-7 rounded-full bg-emerald-100 flex items-center justify-center">
                <Play className="h-3 w-3 text-emerald-700 fill-emerald-700" />
              </span>
            </a>
          </Button>
        </div>
        <div className="flex items-center gap-6 pt-4 text-sm text-slate-500 font-manrope">
          <span className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            Apoio à conformidade eleitoral
          </span>
          <span className="hidden md:flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-emerald-600" />
            Cancele quando quiser
          </span>
        </div>
      </div>

      {/* Hero mockup card — ranking de lideranças */}
      <div className="lg:col-span-5 relative h-[460px] md:h-[520px]">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden shadow-2xl shadow-slate-900/20">
          <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full bg-emerald-500/25 blur-3xl" />
          <div className="absolute bottom-0 -left-10 w-72 h-72 rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="absolute inset-0 p-7 md:p-8 flex flex-col gap-5 text-white font-manrope">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-semibold">Resumo da mobilização</div>
                <h3 className="font-outfit font-bold text-xl md:text-2xl mt-0.5">Top lideranças da semana</h3>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold bg-emerald-500/15 border border-emerald-500/30 px-2.5 py-1 rounded-full">
                ao vivo
              </span>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <HeroStat value="48.350" label="Apoiadores" />
              <HeroStat value="2.184" label="Lideranças" />
              <HeroStat value="312" label="Cidades" />
            </div>

            <div className="flex-1 space-y-2.5 mt-1">
              {[
                { pos: 1, name: 'Henrique Marinho', count: 1298, max: 1300, medal: '🥇', color: 'from-emerald-400 to-emerald-500' },
                { pos: 2, name: 'Tatiana Vasconcelos', count: 986, max: 1300, medal: '🥈', color: 'from-emerald-500 to-teal-500' },
                { pos: 3, name: 'Lucas Mascarenhas', count: 764, max: 1300, medal: '🥉', color: 'from-teal-500 to-cyan-500' },
                { pos: 4, name: 'Renata Cavalcanti', count: 592, max: 1300, medal: null, color: 'from-slate-500 to-slate-600' },
                { pos: 5, name: 'Beatriz Macedo', count: 487, max: 1300, medal: null, color: 'from-slate-500 to-slate-600' },
              ].map((l) => (
                <div key={l.pos} className="grid grid-cols-[20px_28px_1fr_56px] gap-2.5 items-center">
                  <span className="text-sm text-center">
                    {l.medal || <span className="text-slate-500 text-xs font-bold">{l.pos}</span>}
                  </span>
                  <div className={`h-7 w-7 rounded-full bg-gradient-to-br ${l.color} flex items-center justify-center text-[10px] font-bold text-white`}>
                    {l.name.split(' ').map((s) => s[0]).join('').slice(0, 2)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{l.name}</div>
                    <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden mt-1">
                      <div
                        className={`h-full bg-gradient-to-r ${l.color} transition-all`}
                        style={{ width: `${(l.count / l.max) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="text-right text-sm font-bold tabular-nums text-emerald-300">
                    {l.count.toLocaleString('pt-BR')}
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-3 border-t border-slate-700/60 flex items-center justify-between text-[11px] text-slate-400">
              <span>Atualizado agora</span>
              <span className="text-emerald-300 font-medium">+847 apoiadores hoje</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Section>
);

const HeroStat = ({ value, label }) => (
  <div>
    <div className="font-outfit font-bold text-2xl md:text-[1.65rem] leading-none tracking-tight">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1.5">{label}</div>
  </div>
);

// ---------------------------------------------------------------------------
// FLOW — 4-step "Entenda o fluxo completo"
// ---------------------------------------------------------------------------
const FlowSection = () => (
  <Section id="fluxo" className="py-24">
    <div className="bg-slate-50/70 rounded-3xl p-8 md:p-12">
      <div className="max-w-3xl mx-auto text-center mb-14 space-y-4">
        <EyebrowLabel>Fluxo</EyebrowLabel>
        <h2 className="font-outfit font-bold text-4xl md:text-5xl text-slate-900 tracking-tight">
          Entenda o <span className="text-emerald-600">fluxo completo</span>
        </h2>
        <p className="text-lg text-slate-600 font-manrope leading-relaxed">
          Um caminho simples que organiza lideranças, conecta apoiadores e gera inteligência
          para sua campanha — com consentimento e rastreabilidade em cada passo.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 relative">
        <FlowStep
          n="1"
          title="Cadastro da liderança"
          desc="A liderança se cadastra em um link público e passa a fazer parte da rede da campanha."
          mockup={<LeaderFormMockup />}
        />
        <FlowStep
          n="2"
          title="Link personalizado"
          desc="Cada liderança recebe um link e QR Code exclusivos para convidar seus apoiadores."
          mockup={<LeaderCardMockup />}
        />
        <FlowStep
          n="3"
          title="Cadastro do apoiador"
          desc="O apoiador se cadastra com aceite da política de privacidade e é direcionado para o canal escolhido pela campanha."
          mockup={<SupporterFormMockup />}
        />
        <FlowStep
          n="4"
          title="Inteligência para a campanha"
          desc="O administrador acompanha ranking de lideranças e mapa de calor com apoiadores por bairros e cidades."
          mockup={<IntelMockup />}
        />
      </div>
    </div>
  </Section>
);

const FlowStep = ({ n, title, desc, mockup }) => (
  <div className="relative bg-white border border-slate-200 rounded-2xl p-6 hover:border-emerald-300 hover:shadow-lg transition-all" data-testid={`flow-step-${n}`}>
    <div className="absolute -top-3 -left-3 h-9 w-9 rounded-full bg-emerald-500 text-white flex items-center justify-center font-outfit font-bold text-sm shadow-md">
      {n}
    </div>
    <h3 className="font-outfit font-bold text-lg text-slate-900 mb-1.5 mt-1">{title}</h3>
    <p className="text-sm text-slate-600 font-manrope leading-relaxed mb-5">{desc}</p>
    <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
      {mockup}
    </div>
  </div>
);

const LeaderFormMockup = () => (
  <div className="space-y-2">
    <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 mb-2 font-manrope">
      <div className="h-7 w-7 rounded-lg bg-slate-900 flex items-center justify-center">
        <Users className="h-3.5 w-3.5 text-white" />
      </div>
      Cadastro de liderança
    </div>
    {['Nome completo', 'WhatsApp', 'Cidade'].map((p) => (
      <div key={p} className="h-7 rounded-md bg-white border border-slate-200 px-2.5 flex items-center text-[11px] text-slate-400 font-manrope">{p}</div>
    ))}
    <div className="h-7 rounded-md bg-emerald-500 text-white flex items-center justify-center text-[11px] font-semibold font-manrope mt-2">Cadastrar liderança</div>
  </div>
);

const LeaderCardMockup = () => (
  <div className="space-y-2">
    <div className="flex items-center gap-2">
      <div className="h-9 w-9 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white text-[10px] font-bold">JS</div>
      <div className="text-[11px] font-manrope">
        <div className="font-bold text-slate-900">João Silva</div>
        <div className="text-slate-500">Liderança</div>
      </div>
    </div>
    <div className="bg-white rounded-md border border-slate-200 px-2 py-1.5 flex items-center justify-between gap-2">
      <span className="text-[10px] font-mono text-slate-600 truncate">meadd.app/jooosilva</span>
      <QrCode className="h-3.5 w-3.5 text-slate-400 shrink-0" />
    </div>
    <div className="bg-white rounded-md border border-slate-200 p-1.5 flex items-center justify-center">
      <div className="h-14 w-14 bg-[repeating-conic-gradient(theme(colors.slate.900)_0_25%,_white_0_50%)] [background-size:6px_6px] rounded" />
    </div>
  </div>
);

const SupporterFormMockup = () => (
  <div className="space-y-2.5 text-[11px] font-manrope">
    <div className="font-bold text-slate-900">Junte-se à nossa rede!</div>
    {['Nome completo', 'WhatsApp', 'Cidade'].map((p) => (
      <div key={p} className="h-6 rounded-md bg-white border border-slate-200 px-2 flex items-center text-[10px] text-slate-400">{p}</div>
    ))}
    <div className="h-6 rounded-md bg-white border border-slate-200 px-2 flex items-center justify-between text-[10px] text-slate-400">
      <span>Tema de interesse</span>
      <ChevronDown className="h-3 w-3 text-slate-400" />
    </div>
    <label className="flex items-center gap-1.5 text-[10px] text-slate-600">
      <span className="h-3 w-3 rounded-sm bg-emerald-500 flex items-center justify-center">
        <Check className="h-2 w-2 text-white" />
      </span>
      Aceito a Política de Privacidade
    </label>
    <div className="h-6 rounded-md bg-emerald-500 text-white flex items-center justify-center font-semibold text-[10px]">
      Quero participar!
    </div>

    {/* Destinations after signup */}
    <div className="pt-2.5 mt-1 border-t border-slate-200">
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-2 font-manrope">
        Redirecionado para
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {DESTINATIONS.map((d) => (
          <div key={d.label} className="flex flex-col items-center gap-1">
            <div className={`h-7 w-7 rounded-full flex items-center justify-center ${d.bg}`}>
              {d.icon}
            </div>
            <span className="text-[8px] text-slate-600 font-manrope leading-tight text-center">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Brand-style channel icons for the supporter destination panel
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-white" aria-hidden="true">
    <path d="M17.5 14.4c-.3-.1-1.8-.9-2.1-1-.3-.1-.5-.1-.7.2-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5-.1-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.3 5.2 4.6.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.5.3-.7.3-1.4.2-1.5-.1-.1-.3-.2-.6-.3zM12 2C6.5 2 2 6.5 2 12c0 1.9.5 3.7 1.5 5.3L2 22l4.8-1.5c1.5.9 3.3 1.4 5.2 1.4 5.5 0 10-4.5 10-10S17.5 2 12 2z"/>
  </svg>
);
const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-white" aria-hidden="true">
    <path d="M21.5 4.3l-3 14.1c-.2 1-.8 1.2-1.6.8l-4.5-3.3-2.2 2.1c-.2.2-.4.4-.9.4l.3-4.6 8.4-7.6c.4-.3-.1-.5-.6-.2L6.1 12.6 1.6 11.2c-1-.3-1-1 .2-1.5L20 2.5c.8-.3 1.5.2 1.5 1.8z"/>
  </svg>
);
const SiteIcon = () => (
  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9"/>
    <path d="M3 12h18"/>
    <path d="M12 3a13 13 0 010 18M12 3a13 13 0 000 18"/>
  </svg>
);

const DESTINATIONS = [
  { label: 'WhatsApp', bg: 'bg-emerald-500', icon: <WhatsAppIcon /> },
  { label: 'Grupo', bg: 'bg-blue-500', icon: <Users className="h-3.5 w-3.5 text-white" /> },
  { label: 'Canal', bg: 'bg-violet-500', icon: <TelegramIcon /> },
  { label: 'Site', bg: 'bg-slate-700', icon: <SiteIcon /> },
  { label: 'Outro', bg: 'bg-emerald-400', icon: <LinkIcon className="h-3.5 w-3.5 text-white" /> },
];

const IntelMockup = () => (
  <div className="space-y-2.5 text-[10px] font-manrope">
    <div className="flex items-center justify-center mb-2">
      <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
        <BarChart3 className="h-6 w-6 text-emerald-700" />
      </div>
    </div>
    {[
      'Ranking de lideranças',
      'Mapa de calor',
      'Apoiadores por cidade',
      'Apoiadores por bairro',
      'Desempenho em tempo real',
    ].map((item) => (
      <div key={item} className="flex items-center gap-1.5 text-slate-700">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
        <span className="truncate">{item}</span>
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// ADMIN VIEW (Ranking + Heatmap mockups)
// ---------------------------------------------------------------------------
const AdminView = () => (
  <Section id="recursos" className="py-24">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
      <div className="lg:col-span-3 space-y-5 flex flex-col">
        <EyebrowLabel>Painel administrativo</EyebrowLabel>
        <h2 className="font-outfit font-bold text-4xl lg:text-[2.5rem] leading-[1.05] text-slate-900 tracking-tight">
          Visão completa <span className="text-emerald-600">para quem administra</span>
        </h2>
        <p className="text-base text-slate-600 font-manrope leading-relaxed">
          Painéis inteligentes mostram onde sua campanha está crescendo e quais lideranças
          estão gerando mais impacto.
        </p>
        <div className="space-y-5 pt-2">
          <FeatureRow
            icon={Trophy}
            title="Ranking de lideranças"
            desc="Acompanhe quem mais mobiliza apoiadores e engaja novos participantes."
          />
          <FeatureRow
            icon={MapPin}
            title="Mapa de calor"
            desc="Visualize a concentração de apoiadores por bairros e cidades para direcionar ações com mais precisão."
          />
        </div>
      </div>

      <div className="lg:col-span-4 flex">
        <RankingMockup />
      </div>
      <div className="lg:col-span-5 flex">
        <HeatmapMockup />
      </div>
    </div>
  </Section>
);

const FeatureRow = ({ icon: Icon, title, desc }) => (
  <div className="flex items-start gap-3">
    <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
      <Icon className="h-5 w-5 text-emerald-700" />
    </div>
    <div>
      <h4 className="font-outfit font-semibold text-slate-900">{title}</h4>
      <p className="text-sm text-slate-600 font-manrope leading-relaxed mt-0.5">{desc}</p>
    </div>
  </div>
);

const MEDAL_BADGES = {
  1: { bg: 'bg-amber-400', text: 'text-white', label: '1' },
  2: { bg: 'bg-slate-300', text: 'text-slate-700', label: '2' },
  3: { bg: 'bg-orange-400', text: 'text-white', label: '3' },
};

const TrophyBadge = ({ pos }) => {
  const cfg = MEDAL_BADGES[pos];
  if (!cfg) {
    return (
      <div className="h-7 w-7 flex items-center justify-center text-slate-400 text-sm font-semibold font-manrope">
        {pos}
      </div>
    );
  }
  return (
    <div className={`h-7 w-7 rounded-md ${cfg.bg} ${cfg.text} flex items-center justify-center font-bold text-xs shadow-sm relative font-outfit`} title={`${pos}º lugar`}>
      <span className="absolute -top-1 left-1 h-1.5 w-1.5 bg-white/50 rounded-full" />
      <span className="absolute -top-1 right-1 h-1.5 w-1.5 bg-white/50 rounded-full" />
      {cfg.label}
    </div>
  );
};

const RANKING_DATA = [
  { pos: 1, name: 'Henrique Marinho', count: '1.298', initials: 'HM', tone: 'from-blue-500 to-indigo-600' },
  { pos: 2, name: 'Tatiana Vasconcelos', count: '986', initials: 'TV', tone: 'from-rose-400 to-pink-500' },
  { pos: 3, name: 'Lucas Mascarenhas', count: '764', initials: 'LM', tone: 'from-amber-400 to-orange-500' },
  { pos: 4, name: 'Renata Cavalcanti', count: '592', initials: 'RC', tone: 'from-emerald-400 to-teal-500' },
  { pos: 5, name: 'Beatriz Macedo', count: '487', initials: 'BM', tone: 'from-violet-400 to-purple-500' },
];

const RankingMockup = () => (
  <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm w-full flex flex-col">
    <div className="flex items-center justify-between mb-5">
      <h4 className="font-outfit font-bold text-slate-900 text-lg">Ranking de lideranças</h4>
      <span className="text-xs font-medium text-slate-600 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-full font-manrope flex items-center gap-1.5">
        Todas as cidades <ChevronDown className="h-3 w-3" />
      </span>
    </div>
    <div className="grid grid-cols-[40px_1fr_72px] gap-3 text-[10px] uppercase tracking-wider text-slate-400 font-semibold font-manrope pb-3 border-b border-slate-100">
      <span>Posição</span>
      <span>Liderança</span>
      <span className="text-right">Apoiadores</span>
    </div>
    <div className="flex-1 divide-y divide-slate-100">
      {RANKING_DATA.map((r) => (
        <div key={r.pos} className="grid grid-cols-[40px_1fr_72px] gap-3 items-center py-3.5 font-manrope">
          <TrophyBadge pos={r.pos} />
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={`h-8 w-8 rounded-full bg-gradient-to-br ${r.tone} flex items-center justify-center text-[10px] font-bold text-white shrink-0`}>
              {r.initials}
            </div>
            <span className="text-slate-800 font-medium truncate">{r.name}</span>
          </div>
          <span className="text-right tabular-nums font-semibold text-slate-700">{r.count}</span>
        </div>
      ))}
    </div>
    <div className="pt-5 flex justify-center">
      <button
        type="button"
        className="text-sm font-medium text-slate-700 hover:text-slate-900 bg-white border border-slate-200 hover:border-slate-300 px-4 py-2 rounded-full font-manrope transition-colors"
        data-testid="admin-ranking-cta"
      >
        Ver ranking completo
      </button>
    </div>
  </div>
);

const HeatmapMockup = () => (
  <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm w-full flex flex-col">
    <div className="flex items-center justify-between mb-5">
      <h4 className="font-outfit font-bold text-slate-900 text-lg">Mapa de calor</h4>
      <span className="text-xs font-medium text-slate-600 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-full font-manrope flex items-center gap-1.5">
        Todas as cidades <ChevronDown className="h-3 w-3" />
      </span>
    </div>
    <div className="relative flex-1 min-h-[280px] rounded-xl bg-[#e8f1e8] overflow-hidden border border-slate-100">
      <svg viewBox="0 0 360 240" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full">
        <defs>
          <radialGradient id="heat-hot" cx="50%" cy="50%">
            <stop offset="0%" stopColor="#ef4444" stopOpacity="0.95" />
            <stop offset="35%" stopColor="#f59e0b" stopOpacity="0.7" />
            <stop offset="70%" stopColor="#facc15" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#a7f3d0" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="heat-warm" cx="50%" cy="50%">
            <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.85" />
            <stop offset="50%" stopColor="#facc15" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#a7f3d0" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="heat-mid" cx="50%" cy="50%">
            <stop offset="0%" stopColor="#84cc16" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#a7f3d0" stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* Park / green areas */}
        <path d="M0,160 Q60,140 90,170 T200,200 L0,240 Z" fill="#bee3b5" opacity="0.5" />
        <ellipse cx="290" cy="60" rx="55" ry="32" fill="#cfe8c3" opacity="0.5" />
        {/* River (light blue) */}
        <path d="M-10,30 C80,55 140,140 220,150 S330,205 370,200" stroke="#bfdbfe" strokeWidth="14" fill="none" opacity="0.55" strokeLinecap="round" />
        {/* Streets grid (white lines) */}
        <g stroke="white" strokeWidth="2.4" opacity="0.85">
          <path d="M-10,55 Q90,40 200,75 T370,80" fill="none" />
          <path d="M-10,120 Q120,110 240,135 T370,150" fill="none" />
          <path d="M-10,180 Q100,170 220,195 T370,210" fill="none" />
          <path d="M40,-10 Q60,80 90,170 T120,250" fill="none" />
          <path d="M140,-10 Q170,80 180,160 T200,250" fill="none" />
          <path d="M240,-10 Q260,80 280,160 T300,250" fill="none" />
        </g>
        <g stroke="white" strokeWidth="1.2" opacity="0.6">
          {[10, 70, 130, 190, 250, 310].map((y) => <line key={y} x1="0" y1={y} x2="360" y2={y} />)}
          {[20, 80, 160, 220, 280, 340].map((x) => <line key={x} x1={x} y1="0" x2={x} y2="240" />)}
        </g>
        {/* Heat blobs */}
        <circle cx="220" cy="80" r="58" fill="url(#heat-hot)" />
        <circle cx="270" cy="160" r="50" fill="url(#heat-hot)" />
        <circle cx="140" cy="130" r="38" fill="url(#heat-warm)" />
        <circle cx="60" cy="60" r="28" fill="url(#heat-warm)" />
        <circle cx="320" cy="80" r="22" fill="url(#heat-mid)" />
        <circle cx="80" cy="180" r="26" fill="url(#heat-mid)" />
        <circle cx="180" cy="195" r="20" fill="url(#heat-mid)" />
      </svg>
      {/* Zoom controls */}
      <div className="absolute right-3 bottom-3 flex flex-col rounded-md overflow-hidden shadow-md border border-slate-200 bg-white">
        <button className="h-7 w-7 flex items-center justify-center text-slate-700 hover:bg-slate-50 text-base font-semibold border-b border-slate-200">+</button>
        <button className="h-7 w-7 flex items-center justify-center text-slate-700 hover:bg-slate-50 text-base font-semibold">−</button>
      </div>
    </div>
    <div className="mt-5 flex items-center gap-4 text-xs font-manrope flex-wrap">
      <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" /> <span className="text-slate-600">Baixo</span></span>
      <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /> <span className="text-slate-600">Médio</span></span>
      <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-orange-500" /> <span className="text-slate-600">Alto</span></span>
      <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-500" /> <span className="text-slate-600">Muito alto</span></span>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// NETWORK — Visualização da rede de lideranças
// ---------------------------------------------------------------------------
// Coordinates are tuned for the 320x280 viewBox
const NETWORK_NODES = [
  { id: 'renato', name: 'Renato Almeida', x: 168, y: 36, type: 'lead' },
  { id: 'camila', name: 'Camila Ferreira', x: 198, y: 70, type: 'lead' },
  { id: 'bruno', name: 'Bruno Tavares', x: 232, y: 64, type: 'lead' },
  { id: 'larissa', name: 'Larissa Mendes', x: 92, y: 92, type: 'lead' },
  { id: 'rafael', name: 'Rafael Coutinho', x: 148, y: 102, type: 'lead' },
  { id: 'juliana', name: 'Juliana Prado', x: 56, y: 116, type: 'lead' },
  { id: 'diego', name: 'Diego Bittencourt', x: 256, y: 116, type: 'lead' },
  { id: 'patricia', name: 'Patrícia Nogueira', x: 210, y: 132, type: 'lead' },
  { id: 'felipe', name: 'Felipe Andrade', x: 122, y: 158, type: 'lead' },
  { id: 'henrique', name: 'Henrique Marinho', x: 184, y: 178, type: 'strong' },
  { id: 'aline', name: 'Aline Carvalho', x: 244, y: 182, type: 'lead' },
  { id: 'vinicius', name: 'Vinícius Lopes', x: 100, y: 200, type: 'lead' },
  { id: 'beatriz', name: 'Beatriz Macedo', x: 156, y: 214, type: 'lead' },
  { id: 'eduardo', name: 'Eduardo Rocha', x: 210, y: 224, type: 'lead' },
  { id: 'mariana', name: 'Mariana Brandão', x: 60, y: 224, type: 'support' },
  { id: 'gustavo', name: 'Gustavo Pires', x: 140, y: 252, type: 'lead' },
];

const NETWORK_EDGES = [
  ['larissa', 'juliana'],
  ['larissa', 'rafael'],
  ['rafael', 'felipe'],
  ['rafael', 'patricia'],
  ['camila', 'renato'],
  ['camila', 'bruno'],
  ['bruno', 'diego'],
  ['patricia', 'henrique'],
  ['henrique', 'aline'],
  ['henrique', 'beatriz'],
  ['beatriz', 'eduardo'],
  ['beatriz', 'vinicius'],
  ['vinicius', 'mariana'],
  ['beatriz', 'gustavo'],
];

const TOP_APOIADORES = [
  { pos: 1, name: 'Henrique Marinho', city: 'Belo Horizonte', count: '142' },
  { pos: 2, name: 'Tatiana Vasconcelos', city: 'Curitiba', count: '128' },
  { pos: 3, name: 'Lucas Mascarenhas', city: 'Salvador', count: '116' },
  { pos: 4, name: 'Renata Cavalcanti', city: 'Fortaleza', count: '104' },
  { pos: 5, name: 'Beatriz Macedo', city: 'Porto Alegre', count: '97' },
];

const NodeDot = ({ x, y, type }) => {
  const cls =
    type === 'strong'
      ? 'fill-emerald-400 stroke-emerald-200'
      : type === 'support'
      ? 'fill-blue-400 stroke-blue-200'
      : 'fill-emerald-500 stroke-emerald-300';
  return (
    <>
      <circle cx={x} cy={y} r="6.5" className={`${cls} stroke-[1.5]`} />
      <circle cx={x} cy={y} r="3.5" fill="#0f172a" />
    </>
  );
};

const NetworkSection = () => (
  <Section className="py-24">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
      <div className="lg:col-span-4 flex flex-col gap-5">
        <EyebrowLabel>Rede de relacionamentos</EyebrowLabel>
        <h2 className="font-outfit font-bold text-4xl lg:text-[2.5rem] leading-[1.05] text-slate-900 tracking-tight">
          Cada conexão da sua <span className="text-emerald-600">campanha no lugar certo</span>
        </h2>
        <p className="text-base text-slate-600 font-manrope leading-relaxed">
          Visualize a teia completa de lideranças e apoiadores em um único diagrama.
          Identifique conectores, descubra grupos isolados e tome decisões com base em
          quem realmente está mobilizando.
        </p>
        <div className="space-y-4 pt-1">
          <FeatureRow
            icon={Network}
            title="Mapa de relacionamentos"
            desc="Cada nó representa uma liderança ou apoiador. As linhas mostram quem trouxe quem."
          />
          <FeatureRow
            icon={Sparkles}
            title="Insights automáticos"
            desc="A plataforma destaca hubs influentes, conexões frágeis e oportunidades de crescimento."
          />
          <FeatureRow
            icon={Trophy}
            title="Top apoiadores por centralidade"
            desc="Descubra quem está no centro da sua rede — potenciais futuras lideranças."
          />
        </div>
      </div>

      <div className="lg:col-span-8 flex">
        <NetworkMockup />
      </div>
    </div>
  </Section>
);

const NetworkMockup = () => (
  <div className="w-full bg-white border border-slate-200 rounded-2xl p-5 md:p-6 shadow-sm flex flex-col">
    <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
      <div>
        <h4 className="font-outfit font-bold text-slate-900 text-lg">Mapa de Relacionamentos</h4>
        <p className="text-[11px] text-slate-500 font-manrope mt-0.5">
          Arraste · Role para zoom · Clique para focar
        </p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {['Todos os estados', 'Cidade', 'Todos'].map((p) => (
          <span key={p} className="text-[11px] font-medium text-slate-600 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-full font-manrope flex items-center gap-1.5">
            {p} <ChevronDown className="h-3 w-3" />
          </span>
        ))}
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-[160px_1fr_180px] gap-4 flex-1">
      {/* Left rail — overview */}
      <div className="space-y-4">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold font-manrope">
            Visão geral
          </div>
          <div className="font-outfit font-bold text-3xl text-slate-900 mt-1">2.471</div>
          <div className="text-[10px] text-slate-500 font-manrope">Total de contatos</div>
        </div>
        <div className="space-y-2 text-xs font-manrope">
          <StatRow color="bg-slate-900" label="Lideranças" value="184" />
          <StatRow color="bg-blue-500" label="Apoiadores" value="2.287" />
        </div>
        <div className="border-t border-slate-100 pt-3 space-y-2 text-xs font-manrope">
          <div className="flex justify-between text-slate-600"><span>Conexões</span><span className="font-semibold text-slate-900">3.842</span></div>
          <div className="flex justify-between text-slate-600"><span>Grupos</span><span className="font-semibold text-slate-900">12</span></div>
          <div className="flex justify-between text-slate-600">
            <span>Densidade</span>
            <span className="font-semibold text-emerald-600">0.82 alta</span>
          </div>
        </div>
      </div>

      {/* Center graph */}
      <div className="relative bg-slate-50/70 border border-slate-100 rounded-xl overflow-hidden min-h-[320px]">
        {/* dotted background */}
        <svg viewBox="0 0 320 280" preserveAspectRatio="xMidYMid meet" className="absolute inset-0 w-full h-full">
          <defs>
            <pattern id="dotgrid" width="16" height="16" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="0.7" fill="#cbd5e1" opacity="0.6" />
            </pattern>
          </defs>
          <rect width="320" height="280" fill="url(#dotgrid)" />
          {/* edges */}
          <g stroke="#94a3b8" strokeWidth="0.7" opacity="0.55">
            {NETWORK_EDGES.map(([a, b], idx) => {
              const na = NETWORK_NODES.find((n) => n.id === a);
              const nb = NETWORK_NODES.find((n) => n.id === b);
              if (!na || !nb) return null;
              return <line key={idx} x1={na.x} y1={na.y} x2={nb.x} y2={nb.y} />;
            })}
          </g>
          {/* nodes */}
          {NETWORK_NODES.map((n) => (
            <g key={n.id}>
              <NodeDot x={n.x} y={n.y} type={n.type} />
              <text
                x={n.x}
                y={n.y + 17}
                textAnchor="middle"
                className="fill-slate-700"
                style={{ fontSize: 7, fontFamily: 'Manrope, system-ui', fontWeight: 600 }}
              >
                {n.name}
              </text>
            </g>
          ))}
        </svg>
        {/* legend */}
        <div className="absolute left-3 bottom-3 bg-white/90 backdrop-blur-sm border border-slate-200 rounded-md px-2.5 py-2 text-[10px] font-manrope space-y-1 shadow-sm">
          <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500 border border-emerald-300" /> Liderança</div>
          <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-400 border border-emerald-200" /> Apoiador forte</div>
          <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-blue-400 border border-blue-200" /> Apoiador</div>
        </div>
      </div>

      {/* Right rail — top apoiadores */}
      <div className="space-y-4">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold font-manrope">
            Insights da rede
          </div>
          <p className="text-[11px] text-slate-700 font-manrope mt-2 leading-snug">
            <strong>&ldquo;Henrique Marinho&rdquo;</strong> é seu apoiador mais central — conecta 142 outros membros da rede.
          </p>
        </div>
        <div className="border-t border-slate-100 pt-3">
          <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold font-manrope mb-2.5">
            Top apoiadores
          </div>
          <ul className="space-y-2.5">
            {TOP_APOIADORES.map((p) => (
              <li key={p.pos} className="flex items-center gap-2.5 text-xs font-manrope">
                <span className="h-6 w-6 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center font-bold text-[10px] shrink-0">
                  {p.pos}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-800 truncate">{p.name}</div>
                  <div className="text-[10px] text-slate-500">{p.city}</div>
                </div>
                <span className="text-[10px] font-semibold text-emerald-700 tabular-nums shrink-0">{p.count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </div>
);

const StatRow = ({ color, label, value }) => (
  <div className="flex items-center justify-between">
    <span className="flex items-center gap-2 text-slate-600">
      <span className={`h-2 w-2 rounded-full ${color}`} />
      {label}
    </span>
    <span className="font-semibold text-slate-900">{value}</span>
  </div>
);

// ---------------------------------------------------------------------------
// INLINE CTA — band shown between sections to repeat the trial offer
// ---------------------------------------------------------------------------
const InlineCTA = ({ headline = 'Pronto para experimentar?', subline = 'Crie sua conta em 30 segundos. 7 dias grátis. Sem cartão.' }) => (
  <Section className="py-10">
    <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-7 md:p-9 relative overflow-hidden">
      <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full bg-amber-400/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-8 -left-8 w-48 h-48 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
      <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="text-white">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-amber-300 mb-1.5">
            ✨ Comece grátis · Sem cartão de crédito
          </div>
          <h3 className="font-outfit font-bold text-2xl md:text-3xl tracking-tight leading-snug">{headline}</h3>
          <p className="text-slate-400 font-manrope text-sm md:text-base mt-1.5 max-w-xl">{subline}</p>
        </div>
        <Button asChild size="lg" className="bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-slate-900 font-manrope font-bold rounded-full px-7 h-12 shadow-lg shadow-amber-500/30 hover:shadow-xl hover:shadow-amber-500/40 hover:-translate-y-0.5 transition-all whitespace-nowrap">
          <Link to="/signup">
            Testar grátis 7 dias <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  </Section>
);

// ---------------------------------------------------------------------------
// FINANCIAL — Gestão Orçamentária (Pro)
// ---------------------------------------------------------------------------
const BUDGET_AREAS = [
  { area: 'Marketing & Mídia', planned: 180000, spent: 168400, color: 'bg-rose-500', alert: 'critical' },
  { area: 'Eventos & Comícios', planned: 120000, spent: 78600, color: 'bg-amber-500', alert: null },
  { area: 'Material gráfico', planned: 95000, spent: 52800, color: 'bg-emerald-500', alert: null },
  { area: 'Equipe & RH', planned: 220000, spent: 192300, color: 'bg-blue-500', alert: 'warning' },
  { area: 'Logística', planned: 85000, spent: 41200, color: 'bg-violet-500', alert: null },
];

const RECENT_EXPENSES = [
  { date: '07/06', desc: 'Impulsionamento Meta Ads — Goiânia', area: 'Marketing', value: 4800, tipo: 'NFe' },
  { date: '06/06', desc: 'Locação som — comício BH', area: 'Eventos', value: 3200, tipo: 'Recibo' },
  { date: '05/06', desc: 'Folhetos campanha (10k un.)', area: 'Material', value: 2150, tipo: 'NFe' },
  { date: '05/06', desc: 'Salário coordenação regional', area: 'Equipe', value: 8500, tipo: 'RPA' },
];

const FinancialSection = () => {
  const totalPlanned = BUDGET_AREAS.reduce((s, a) => s + a.planned, 0);
  const totalSpent = BUDGET_AREAS.reduce((s, a) => s + a.spent, 0);
  const pct = Math.round((totalSpent / totalPlanned) * 100);

  return (
    <Section className="py-24">
      <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
        <div className="inline-flex items-center gap-2">
          <EyebrowLabel>Módulo financeiro</EyebrowLabel>
          <Badge className="bg-amber-100 text-amber-800 border border-amber-300 font-bold">Disponível no Pro</Badge>
        </div>
        <h2 className="font-outfit font-bold text-4xl md:text-5xl text-slate-900 tracking-tight">
          Gestão orçamentária <span className="text-emerald-600">dentro da campanha</span>
        </h2>
        <p className="text-lg text-slate-600 font-manrope leading-relaxed">
          Planeje seu orçamento por área, registre cada gasto com comprovante, receba alertas
          estratégicos quando algo sair do controle e exporte tudo para sua equipe contábil.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch">
        {/* Big card — budget allocation */}
        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
            <div>
              <h4 className="font-outfit font-bold text-slate-900 text-lg">Orçamento por área</h4>
              <p className="text-xs text-slate-500 font-manrope mt-0.5">Campanha 2026 — Período jun/2026</p>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 font-manrope">Executado / Planejado</div>
              <div className="font-outfit font-bold text-xl text-slate-900">
                R$ {(totalSpent / 1000).toFixed(0)}k <span className="text-slate-400 text-base font-medium">/ R$ {(totalPlanned / 1000).toFixed(0)}k</span>
              </div>
              <div className={`text-xs font-semibold font-manrope ${pct > 90 ? 'text-rose-600' : pct > 75 ? 'text-amber-600' : 'text-emerald-600'}`}>{pct}% executado</div>
            </div>
          </div>
          <div className="space-y-3.5 flex-1">
            {BUDGET_AREAS.map((b) => {
              const usedPct = Math.round((b.spent / b.planned) * 100);
              return (
                <div key={b.area} className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm font-manrope">
                    <span className="text-slate-800 font-medium flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${b.color}`} />
                      {b.area}
                      {b.alert === 'critical' && <AlertTriangleIcon className="h-3.5 w-3.5 text-rose-500" />}
                      {b.alert === 'warning' && <AlertTriangleIcon className="h-3.5 w-3.5 text-amber-500" />}
                    </span>
                    <span className="text-slate-500 text-xs tabular-nums">
                      R$ {(b.spent / 1000).toFixed(1)}k / {(b.planned / 1000).toFixed(0)}k · <strong className={usedPct >= 90 ? 'text-rose-600' : usedPct >= 75 ? 'text-amber-600' : 'text-emerald-700'}>{usedPct}%</strong>
                    </span>
                  </div>
                  <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${b.color} transition-all`} style={{ width: `${Math.min(usedPct, 100)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Alerts panel */}
        <div className="lg:col-span-5 bg-slate-900 text-white rounded-2xl p-6 shadow-sm flex flex-col relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-60 h-60 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col flex-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-8 w-8 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                <AlertTriangleIcon className="h-4 w-4 text-amber-300" />
              </div>
              <div>
                <h4 className="font-outfit font-bold text-lg">Alertas estratégicos</h4>
                <p className="text-[11px] text-slate-400 font-manrope">Atualizado agora</p>
              </div>
            </div>
            <div className="space-y-3 flex-1">
              <AlertItem
                tone="rose"
                title="Marketing & Mídia já consumiu 93,5% do orçamento"
                desc="Faltam 24 dias para o fim do mês. Reveja o impulsionamento em Meta Ads."
              />
              <AlertItem
                tone="amber"
                title="Equipe & RH no limite (87,4%)"
                desc="Considere remanejar dotação ou reduzir contratações pontuais."
              />
              <AlertItem
                tone="emerald"
                title="3 comprovantes pendentes de anexo"
                desc="Gastos sem nota/recibo geram pendência para o relatório contábil."
              />
              <AlertItem
                tone="emerald"
                title="Logística com folga (48,5%)"
                desc="Possível remanejar até R$ 18.000 para outra área."
              />
            </div>
            <Link
              to="/signup"
              className="mt-5 inline-flex items-center justify-center gap-2 text-sm font-semibold text-amber-300 hover:text-amber-200 font-manrope"
            >
              Habilitar no plano Pro <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* Recent expenses table */}
        <div className="lg:col-span-12 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div>
              <h4 className="font-outfit font-bold text-slate-900 text-lg">Gastos recentes</h4>
              <p className="text-xs text-slate-500 font-manrope mt-0.5">CPF/CNPJ identificado · comprovante anexado · pronto para conferência</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium text-slate-600 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-full font-manrope">Todas as áreas</span>
              <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full font-manrope">Junho/2026</span>
            </div>
          </div>
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm font-manrope">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold border-b border-slate-100">
                  <th className="text-left py-2 px-2 w-16">Data</th>
                  <th className="text-left py-2 px-2">Descrição</th>
                  <th className="text-left py-2 px-2 hidden sm:table-cell">Área</th>
                  <th className="text-left py-2 px-2 hidden sm:table-cell">Doc.</th>
                  <th className="text-right py-2 px-2">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {RECENT_EXPENSES.map((e, idx) => (
                  <tr key={idx}>
                    <td className="py-3 px-2 text-slate-500 tabular-nums">{e.date}</td>
                    <td className="py-3 px-2 text-slate-800">{e.desc}</td>
                    <td className="py-3 px-2 hidden sm:table-cell">
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 bg-slate-100 px-2 py-1 rounded-full">{e.area}</span>
                    </td>
                    <td className="py-3 px-2 hidden sm:table-cell">
                      <span className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1">
                        <Check className="h-3 w-3" /> {e.tipo}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right font-semibold tabular-nums text-slate-900">
                      R$ {e.value.toLocaleString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Feature highlights */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-10">
        <FinFeature icon={Wallet} title="Planejamento por área" desc="Aloque 100% do orçamento entre as áreas da campanha. Validação automática." />
        <FinFeature icon={ScrollText} title="Anexo de comprovantes" desc="Cada gasto leva NFe, RPA ou recibo. CPF/CNPJ identificado automaticamente." />
        <FinFeature icon={AlertTriangleIcon} title="Alertas estratégicos" desc="Avisos quando uma área ultrapassa 75%, 90% ou estoura o orçado." />
        <FinFeature icon={FileSpreadsheet} title="Exportação Excel" desc="Planilha multi-aba pronta para jurídico, contábil e prestação interna." />
      </div>
    </Section>
  );
};

const AlertTriangleIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const AlertItem = ({ tone, title, desc }) => {
  const colors = {
    rose: 'bg-rose-500/15 border-rose-500/30 text-rose-200',
    amber: 'bg-amber-500/15 border-amber-500/30 text-amber-200',
    emerald: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-200',
  }[tone];
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${colors}`}>
      <div className="text-sm font-semibold font-manrope leading-snug">{title}</div>
      <div className="text-xs text-slate-300 font-manrope mt-1 leading-snug">{desc}</div>
    </div>
  );
};

const FinFeature = ({ icon: Icon, title, desc }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 hover:border-emerald-300 hover:shadow-md transition-all">
    <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-3">
      <Icon className="h-5 w-5 text-emerald-700" />
    </div>
    <h4 className="font-outfit font-semibold text-slate-900">{title}</h4>
    <p className="text-sm text-slate-600 font-manrope leading-relaxed mt-1">{desc}</p>
  </div>
);

// ---------------------------------------------------------------------------
// MODULES
// ---------------------------------------------------------------------------
const Modules = () => (
  <Section className="py-24">
    <div className="max-w-3xl mb-14 space-y-4">
      <EyebrowLabel>Módulos</EyebrowLabel>
      <h2 className="font-outfit font-bold text-4xl md:text-5xl text-slate-900 tracking-tight">
        Dois módulos para uma <span className="text-emerald-600">operação mais organizada</span>
      </h2>
      <p className="text-lg text-slate-600 font-manrope leading-relaxed">
        Mobilização e controle financeiro em um só ambiente, com registros, permissões e
        relatórios de apoio à gestão da campanha.
      </p>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ModuleCard
        icon={Zap}
        accent="from-emerald-500 to-emerald-600"
        title="Mobilização"
        intro="Organize a rede de lideranças e apoiadores com links personalizados, cadastros consentidos e acompanhamento por cidade, tema, canal ou responsável."
        bullets={[
          { icon: LinkIcon, text: 'Links e QR Codes por liderança' },
          { icon: Users, text: 'Cadastro voluntário de apoiadores' },
          { icon: Tag, text: 'Registro de origem do cadastro' },
          { icon: MapPin, text: 'Segmentação por cidade, tema ou canal' },
          { icon: Trophy, text: 'Ranking interno de engajamento' },
          { icon: ListChecks, text: 'Histórico de consentimento e descadastro' },
        ]}
      />
      <ModuleCard
        icon={Wallet}
        accent="from-slate-700 to-slate-900"
        title="Financeiro"
        intro="Registre despesas, anexe comprovantes e organize informações financeiras para facilitar a conferência interna e o trabalho das equipes contábil e jurídica."
        bullets={[
          { icon: ScrollText, text: 'Registro interno de despesas' },
          { icon: FileText, text: 'Anexo de comprovantes, recibos e notas' },
          { icon: Tag, text: 'Organização por categoria de gasto' },
          { icon: ShieldCheck, text: 'Alertas de pendência documental' },
          { icon: BarChart3, text: 'Relatórios auxiliares para conferência' },
          { icon: FileSpreadsheet, text: 'Exportação para jurídico, contábil e financeiro' },
        ]}
      />
    </div>
  </Section>
);

const ModuleCard = ({ icon: Icon, accent, title, intro, bullets }) => (
  <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow">
    <div className={`p-6 bg-gradient-to-br ${accent} text-white`}>
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
          <Icon className="h-5 w-5 text-white" />
        </div>
        <h3 className="font-outfit font-bold text-2xl">{title}</h3>
      </div>
      <p className="mt-4 text-white/85 font-manrope leading-relaxed">{intro}</p>
    </div>
    <ul className="p-6 space-y-3">
      {bullets.map((b) => (
        <li key={b.text} className="flex items-start gap-3">
          <div className="h-7 w-7 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
            <b.icon className="h-3.5 w-3.5 text-emerald-700" />
          </div>
          <span className="text-slate-700 font-manrope text-[15px]">{b.text}</span>
        </li>
      ))}
    </ul>
  </div>
);

// ---------------------------------------------------------------------------
// COMPLIANCE
// ---------------------------------------------------------------------------
const Compliance = () => (
  <Section className="py-24">
    <div className="bg-slate-900 rounded-3xl p-10 md:p-16 text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-emerald-500/15 blur-3xl" />
      <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-emerald-500/10 blur-3xl" />
      <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        <div>
          <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 mb-6">
            Conformidade & Governança
          </Badge>
          <h2 className="font-outfit font-bold text-4xl md:text-5xl tracking-tight mb-6 leading-[1.1]">
            Conformidade eleitoral <span className="text-emerald-400">desde a concepção</span>
          </h2>
          <div className="space-y-4 text-slate-300 font-manrope leading-relaxed">
            <p>
              O MeAdd foi desenhado para apoiar campanhas na organização de dados, mobilização
              e controle interno, respeitando as regras eleitorais de 2026, a LGPD e as boas
              práticas de transparência.
            </p>
            <p>
              A plataforma permite registrar consentimentos, identificar a origem dos
              apoiadores, organizar canais oficiais, controlar permissões de acesso, anexar
              comprovantes financeiros e manter histórico auditável das principais ações da
              campanha.
            </p>
            <p className="text-slate-400 italic text-sm">
              O MeAdd não substitui a assessoria jurídica, contábil ou a prestação de contas
              oficial perante a Justiça Eleitoral, mas ajuda a campanha a trabalhar com
              método, evidência e responsabilidade.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="compliance-checklist">
          {[
            'Cadastro voluntário de apoiadores',
            'Registro de consentimento e finalidade',
            'Opção de descadastro',
            'Identificação da liderança ou canal de origem',
            'Segmentação por interesse declarado',
            'Histórico de ações para auditoria interna',
            'Organização de comprovantes financeiros',
            'Permissões por perfil de usuário',
            'Exportação para conferência jurídica e contábil',
            'Avisos preventivos sobre propaganda eleitoral',
          ].map((item) => (
            <div key={item} className="flex items-start gap-2.5 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5">
              <div className="h-5 w-5 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                <Check className="h-3 w-3 text-emerald-300" />
              </div>
              <span className="font-manrope text-sm text-slate-200 leading-snug">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </Section>
);

// ---------------------------------------------------------------------------
// USO RESPONSÁVEL EM 2026
// ---------------------------------------------------------------------------
const ResponsibleUse = () => (
  <Section className="py-24">
    <div className="max-w-3xl mb-14 space-y-4">
      <EyebrowLabel>Boas práticas</EyebrowLabel>
      <h2 className="font-outfit font-bold text-4xl md:text-5xl text-slate-900 tracking-tight">
        Uso responsável <span className="text-emerald-600">em 2026</span>
      </h2>
      <p className="text-lg text-slate-600 font-manrope leading-relaxed">
        Antes de <strong>16 de agosto</strong>, o MeAdd deve ser utilizado para organização
        de lideranças, escuta, relacionamento, cadastro voluntário de apoiadores e comunicação
        de pré-campanha permitida, sem pedido explícito de voto.
      </p>
      <p className="text-base text-slate-500 font-manrope leading-relaxed">
        A partir do início da propaganda eleitoral, a campanha deve observar as regras
        aplicáveis à propaganda na internet, impulsionamento, identificação de conteúdos,
        prestação de contas, proteção de dados pessoais e demais normas eleitorais.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      <PhaseCard
        tag="Antes de 16/ago"
        title="Pré-campanha"
        accent="bg-blue-50 text-blue-700 border-blue-200"
        desc="Organização, escuta, cadastro voluntário, segmentação temática e relacionamento com lideranças, sem pedido explícito de voto."
        icon={Users}
      />
      <PhaseCard
        tag="A partir de 16/ago"
        title="Campanha"
        accent="bg-emerald-50 text-emerald-700 border-emerald-200"
        desc="Comunicação eleitoral dentro do período permitido, acompanhamento de canais, registros de apoiadores e gestão de evidências."
        icon={MessageCircle}
      />
      <PhaseCard
        tag="Pós-eleição"
        title="Prestação e governança"
        accent="bg-amber-50 text-amber-700 border-amber-200"
        desc="Organização de comprovantes, permissões, relatórios auxiliares e histórico para apoio à equipe contábil e jurídica."
        icon={FileSpreadsheet}
      />
    </div>
  </Section>
);

const PhaseCard = ({ tag, title, desc, accent, icon: Icon }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition-shadow">
    <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full border ${accent} font-manrope`}>
      {tag}
    </span>
    <div className="mt-5 flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-slate-900 flex items-center justify-center">
        <Icon className="h-5 w-5 text-white" />
      </div>
      <h3 className="font-outfit font-bold text-xl text-slate-900">{title}</h3>
    </div>
    <p className="mt-3 text-slate-600 font-manrope leading-relaxed text-[15px]">{desc}</p>
  </div>
);

// ---------------------------------------------------------------------------
// PRICING
// ---------------------------------------------------------------------------
const Pricing = () => {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/plans/public`).then((r) => setPlans(r.data || [])).catch(() => {});
  }, []);
  return (
    <Section id="planos" className="py-24">
      <div className="max-w-3xl mb-14 space-y-4">
        <EyebrowLabel>Planos</EyebrowLabel>
        <h2 className="font-outfit font-bold text-4xl md:text-5xl text-slate-900 tracking-tight">
          7 dias grátis. <span className="text-emerald-600">Depois, você escolhe.</span>
        </h2>
        <p className="text-lg text-slate-600 font-manrope leading-relaxed">
          Acesso a todas as funcionalidades nos primeiros 7 dias. Sem cartão de crédito.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
        {plans.map((p) => (
          <PlanCard key={p.id} plan={p} />
        ))}
      </div>
    </Section>
  );
};

const PlanCard = ({ plan }) => {
  const featured = plan.destaque;
  const features = [
    { ok: plan.feature_mobilizacao, label: 'Organização de lideranças e apoiadores' },
    { ok: plan.feature_mobilizacao, label: 'Links e QR Codes personalizados' },
    { ok: plan.feature_financeiro, label: 'Registro interno de despesas e comprovantes' },
    { ok: plan.feature_heatmap, label: 'Mapa de calor por bairros e cidades' },
    { ok: plan.feature_network, label: 'Visualização da rede de lideranças' },
    {
      ok: true,
      label: plan.max_apoiadores
        ? `Até ${plan.max_apoiadores.toLocaleString('pt-BR')} apoiadores`
        : 'Apoiadores ilimitados',
    },
    {
      ok: true,
      label: plan.max_liderancas
        ? `Até ${plan.max_liderancas.toLocaleString('pt-BR')} lideranças`
        : 'Lideranças ilimitadas',
    },
    { ok: true, label: 'Permissões por perfil de usuário' },
    { ok: true, label: 'Relatórios auxiliares + exportação CSV/XLSX' },
    { ok: true, label: 'Histórico rastreável (consentimento e origem)' },
  ];
  return (
    <div
      className={`rounded-3xl p-8 md:p-10 ${
        featured
          ? 'bg-slate-900 text-white shadow-2xl shadow-slate-900/20 relative overflow-hidden'
          : 'bg-white border border-slate-200'
      }`}
      data-testid={`plan-card-${plan.code}`}
    >
      {featured && (
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
      )}
      <div className="relative space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="font-outfit font-bold text-2xl">{plan.name}</h3>
          {featured && (
            <Badge className="bg-emerald-500 text-white border-0">Mais escolhido</Badge>
          )}
        </div>
        <div>
          <div className="flex items-baseline gap-1">
            <span className="font-outfit font-bold text-5xl">{fmtBRL(plan.price_brl)}</span>
            <span className={`font-manrope text-sm ${featured ? 'text-slate-400' : 'text-slate-500'}`}>/mês</span>
          </div>
        </div>
        <Button
          asChild
          size="lg"
          className={`w-full font-manrope rounded-full h-12 font-bold ${
            featured
              ? 'bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-slate-900 shadow-lg shadow-amber-500/40'
              : 'bg-slate-900 hover:bg-slate-800 text-white'
          }`}
        >
          <Link to="/signup">
            Testar grátis 7 dias <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
        <div className="space-y-3 pt-4 border-t border-slate-200/20">
          {features.map((f, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                f.ok ? (featured ? 'bg-emerald-500/30' : 'bg-emerald-100') : (featured ? 'bg-slate-700' : 'bg-slate-100')
              }`}>
                {f.ok ? (
                  <Check className={`h-3 w-3 ${featured ? 'text-emerald-300' : 'text-emerald-700'}`} />
                ) : (
                  <span className={`h-2 w-2 rounded-full ${featured ? 'bg-slate-500' : 'bg-slate-400'}`} />
                )}
              </div>
              <span className={`font-manrope text-sm ${
                f.ok ? '' : (featured ? 'text-slate-500 line-through' : 'text-slate-400 line-through')
              }`}>
                {f.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// FAQ
// ---------------------------------------------------------------------------
const FAQ = () => {
  const [open, setOpen] = useState(0);
  const items = [
    {
      q: 'O MeAdd substitui a assessoria jurídica ou contábil da campanha?',
      a: 'Não. O MeAdd é uma ferramenta de organização, mobilização e controle interno. Ele ajuda a registrar dados, consentimentos, comprovantes e relatórios auxiliares, mas não substitui a orientação jurídica, contábil ou a prestação de contas oficial perante a Justiça Eleitoral.',
    },
    {
      q: 'Posso usar o MeAdd antes do início oficial da campanha?',
      a: 'Sim, desde que o uso esteja limitado à organização de lideranças, relacionamento, escuta, cadastro voluntário e comunicação de pré-campanha permitida, sem pedido explícito de voto e respeitando a legislação eleitoral vigente.',
    },
    {
      q: 'Como funciona o cadastro de apoiadores?',
      a: 'Os apoiadores podem se cadastrar voluntariamente por links ou QR Codes distribuídos por lideranças, com registro de origem, finalidade e aceite da Política de Privacidade. Eles podem solicitar o descadastro a qualquer momento.',
    },
    {
      q: 'O MeAdd faz disparo em massa?',
      a: 'Não. O MeAdd é para organizar cadastros, canais e relacionamentos com apoiadores que tenham interagido voluntariamente com a campanha. O envio de comunicações deve respeitar a legislação eleitoral, a LGPD e as regras das plataformas utilizadas.',
    },
    {
      q: 'O módulo financeiro substitui o sistema oficial de prestação de contas?',
      a: 'Não. O módulo financeiro organiza despesas, comprovantes e relatórios internos para apoiar a conferência da equipe responsável. A prestação de contas oficial deve seguir as regras e sistemas definidos pela Justiça Eleitoral.',
    },
    {
      q: 'Como a plataforma ajuda na segurança da operação?',
      a: 'O MeAdd registra origem dos cadastros, consentimentos, histórico de ações, anexos financeiros e permissões por usuário, criando uma trilha de organização e auditoria interna para a campanha.',
    },
    {
      q: 'Como funciona o pagamento e o cancelamento?',
      a: 'Após o trial de 7 dias, você escolhe o plano (Básico ou Pro) e ativa a assinatura. Pagamento mensal recorrente, sem fidelidade — cancele quando quiser. Antes de cancelar, é possível exportar todos os seus dados.',
    },
  ];
  return (
    <Section id="faq" className="py-24">
      <div className="max-w-3xl mx-auto space-y-4 mb-12 text-center">
        <EyebrowLabel>Perguntas frequentes</EyebrowLabel>
        <h2 className="font-outfit font-bold text-4xl md:text-5xl text-slate-900 tracking-tight">
          Tudo que você precisa saber
        </h2>
      </div>
      <div className="max-w-3xl mx-auto space-y-2">
        {items.map((item, i) => (
          <div
            key={i}
            className="border border-slate-200 rounded-2xl overflow-hidden bg-white"
            data-testid={`faq-${i}`}
          >
            <button
              onClick={() => setOpen(open === i ? -1 : i)}
              className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors gap-4"
              data-testid={`faq-toggle-${i}`}
            >
              <span className="font-outfit font-semibold text-slate-900">{item.q}</span>
              <ChevronDown
                className={`h-5 w-5 text-slate-500 transition-transform shrink-0 ${open === i ? 'rotate-180' : ''}`}
              />
            </button>
            {open === i && (
              <div className="px-5 pb-5 text-slate-600 font-manrope leading-relaxed">
                {item.a}
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
};

// ---------------------------------------------------------------------------
// FINAL CTA — dark strip
// ---------------------------------------------------------------------------
const FinalCTA = () => (
  <Section className="py-24">
    <div className="bg-slate-900 rounded-3xl p-10 md:p-14 text-white relative overflow-hidden">
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-emerald-500/15 blur-3xl" />
      <div className="absolute bottom-0 right-0 w-72 h-72 rounded-full bg-emerald-500/10 blur-3xl" />
      <div className="relative grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
        <div className="space-y-6">
          <h2 className="font-outfit font-bold text-4xl md:text-5xl tracking-tight leading-[1.05]">
            Mobilize com organização. <span className="text-emerald-400">Cresça com inteligência.</span> Decida com dados.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <CTAFeature icon={Users} title="Mais apoiadores com qualidade" desc="Cadastros voluntários e com consentimento." />
            <CTAFeature icon={Network} title="Mais alcance para sua rede" desc="Lideranças engajadas multiplicam resultados." />
            <CTAFeature icon={BarChart3} title="Mais inteligência para decidir" desc="Dados em tempo real para ações certeiras." />
            <CTAFeature icon={ShieldCheck} title="Mais segurança e rastreabilidade" desc="Tudo registrado, com origem, consentimento e contexto." />
          </div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-7 text-center backdrop-blur">
          <h3 className="font-outfit font-bold text-2xl md:text-3xl">Pronto para organizar sua campanha?</h3>
          <Button asChild size="lg" className="mt-5 w-full bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-slate-900 font-manrope font-bold rounded-full h-12 shadow-lg shadow-amber-500/30 hover:shadow-xl hover:shadow-amber-500/40 transition-all">
            <Link to="/signup" data-testid="final-cta-signup">
              Testar grátis agora <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <p className="mt-2.5 text-[11px] uppercase tracking-wider font-semibold text-amber-300 font-manrope">
            ✨ 7 dias grátis · Sem cartão de crédito
          </p>
        </div>
      </div>
    </div>
  </Section>
);

const CTAFeature = ({ icon: Icon, title, desc }) => (
  <div className="space-y-1.5">
    <div className="h-9 w-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
      <Icon className="h-4 w-4 text-emerald-300" />
    </div>
    <div className="font-outfit font-semibold text-white text-sm">{title}</div>
    <div className="text-xs text-slate-400 font-manrope leading-snug">{desc}</div>
  </div>
);

// ---------------------------------------------------------------------------
// FOOTER
// ---------------------------------------------------------------------------
const Footer = () => (
  <footer className="border-t border-slate-200 mt-0 bg-slate-50/50">
    {/* Responsible-use banner */}
    <div className="px-6 md:px-12 lg:px-20 py-6 border-b border-slate-200">
      <div className="max-w-7xl mx-auto flex items-start gap-3" data-testid="responsible-use-banner">
        <div className="h-9 w-9 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
          <ShieldCheck className="h-4 w-4 text-emerald-700" />
        </div>
        <div>
          <div className="font-outfit font-semibold text-slate-900 text-sm">
            Uso responsável e em conformidade com a legislação
          </div>
          <p className="text-xs text-slate-600 font-manrope leading-relaxed mt-1 max-w-3xl">
            O MeAdd é uma ferramenta de organização, mobilização e controle interno para campanhas eleitorais.
            A plataforma não substitui orientação jurídica, contábil ou a prestação de contas oficial perante a Justiça Eleitoral.
          </p>
        </div>
      </div>
    </div>

    <div className="px-6 md:px-12 lg:px-20 py-12">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2 space-y-4">
          <img src="/brand/meadd-logo.png" alt="MeAdd" className="h-9" />
          <p className="text-sm text-slate-600 font-manrope max-w-md leading-relaxed">
            Plataforma de governança operacional para campanhas eleitorais: organização de
            lideranças, cadastro consentido de apoiadores e controle interno de gastos com
            rastreabilidade e evidências.
          </p>
          <p className="text-xs text-slate-500 font-manrope">
            Desenvolvida e operada pela <strong>Smart7 Digital Ltda.</strong>
          </p>
        </div>
        <div className="space-y-3">
          <h4 className="font-outfit font-semibold text-slate-900 text-sm">Produto</h4>
          <ul className="space-y-2 text-sm text-slate-600 font-manrope">
            <li><a href="#recursos" className="hover:text-emerald-600">Recursos</a></li>
            <li><a href="#fluxo" className="hover:text-emerald-600">Como funciona</a></li>
            <li><a href="#planos" className="hover:text-emerald-600">Planos</a></li>
            <li><a href="#faq" className="hover:text-emerald-600">FAQ</a></li>
          </ul>
        </div>
        <div className="space-y-3">
          <h4 className="font-outfit font-semibold text-slate-900 text-sm">Conta</h4>
          <ul className="space-y-2 text-sm text-slate-600 font-manrope">
            <li><Link to="/login" className="hover:text-emerald-600">Entrar</Link></li>
            <li><Link to="/signup" className="hover:text-emerald-600">Criar conta</Link></li>
            <li><Link to="/privacidade" className="hover:text-emerald-600">Política de Privacidade</Link></li>
            <li><Link to="/termos" className="hover:text-emerald-600">Termos de Uso</Link></li>
          </ul>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="max-w-7xl mx-auto pt-10 mt-10 border-t border-slate-200 space-y-3" data-testid="legal-disclaimer">
        <h5 className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 font-manrope flex items-center gap-2">
          <Lock className="h-3 w-3" />
          Aviso de uso responsável
        </h5>
        <p className="text-xs text-slate-500 font-manrope leading-relaxed max-w-4xl">
          O MeAdd é uma ferramenta de organização, mobilização e controle interno para
          campanhas eleitorais. A plataforma <strong>não substitui</strong> orientação
          jurídica, contábil ou a prestação de contas oficial perante a Justiça Eleitoral.
          O uso da ferramenta deve observar a legislação eleitoral vigente, as resoluções
          do TSE aplicáveis às Eleições 2026, a LGPD e as diretrizes das equipes jurídica
          e contábil da campanha.
        </p>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 text-xs text-slate-500 font-manrope pt-3">
          <div className="flex items-center gap-2">
            <Building2 className="h-3.5 w-3.5" />
            Smart7 Digital Ltda · © {new Date().getFullYear()}
          </div>
          <div>Todos os direitos reservados.</div>
        </div>
      </div>
    </div>
  </footer>
);

// ---------------------------------------------------------------------------
// PAGE
// ---------------------------------------------------------------------------
export const LandingPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then(() => { if (mounted) navigate('/dashboard'); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [navigate]);

  return (
    <div className="bg-white text-slate-900 min-h-screen font-manrope antialiased overflow-x-hidden" data-testid="landing-page">
      <Header />
      <main>
        <Hero />
        <FlowSection />
        <InlineCTA
          headline="Comece a organizar sua rede agora"
          subline="Configure sua campanha em minutos e teste o fluxo completo durante 7 dias."
        />
        <AdminView />
        <NetworkSection />
        <FinancialSection />
        <InlineCTA
          headline="Quer ver o módulo financeiro funcionando?"
          subline="Teste por 7 dias e explore planejamento, lançamento de gastos e alertas em tempo real."
        />
        <Modules />
        <Compliance />
        <ResponsibleUse />
        <Pricing />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
